import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {Button, ContentLayout, Grid, Box, SpaceBetween, Header} from "@cloudscape-design/components";
import {Endpoint, sendRequest, handleResponse} from "../../utils/api";
import {useAuth} from "react-oidc-context";
import {useArchitectureDataStore} from "../../data_store/architectureDataStore.ts";
import {CollectionsCards} from "../../reusable_components/filteredCards/collectionsCards";
import {CARD_DEFINITION, TEXTS} from "./cards_config";
import {useNavigate} from "react-router-dom";


const CustomHeader = () => {
    const navigate = useNavigate();

    return (
        <Box padding={{vertical: "xl"}}>
            <Grid
                gridDefinition={[
                    {colspan: {default: 12, s: 8}}
                ]}
            >
                <div>
                    <Box padding="s">
                        <Box
                            fontSize="display-l"
                            fontWeight="bold"
                            variant="h1"
                            padding="n"
                        >
                            Intelligent architecture generator
                        </Box>
                        <Box
                            fontSize="display-l"
                            fontWeight="light"
                        >
                            Cloud architectures, generated from intent
                        </Box>
                        <Box
                            variant={'p'}
                            color="text-body-secondary"
                            margin={{top: "xs", bottom: "l"}}
                        >
                            Describe what you want to build, and get a secure, scalable, AWS-aligned architecture in under a
                            minute.
                            Powered by generative AI and built to follow cloud best practices from day one.
                        </Box>
                        <SpaceBetween
                            direction="horizontal"
                            size="xs"
                        >
                            <Button variant={'primary'} iconName={'gen-ai'} onClick={() => navigate("/new-architecture")}>
                                Generate new architecture
                            </Button>
                        </SpaceBetween>
                    </Box>
                </div>
            </Grid>
        </Box>
    )
}


const ArchitectureCards = ({items, loading, onRefreshClick}) => {
    return <CollectionsCards
        trackBy={'Title'}
        data={items}
        loading={loading}
        texts={TEXTS}
        cardDefinition={CARD_DEFINITION}
        variant={'full-page'}
        stickyHeader={true}
        header={
            <Header
                variant="h1"
                counter={'(' + items.length + ')'}
                description='Click an item’s title to open its details page and make updates, or click the architecture diagram to enlarge it.'
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button iconName="refresh" loading={loading} onClick={() => onRefreshClick()} />
                    </SpaceBetween>
                }
            >
                Saved architectures
            </Header>
        }
    />
}


export const Home = () => {
    const auth = useAuth();

    const [loading, setLoading] = useState(false);
    const architectureDataStore = useArchitectureDataStore();
    const items = useArchitectureDataStore((s) => s.items);

    const architectures = useMemo(
        () => items.filter(item => item.id !== 'CANVAS'),
        [items]
    );

    useEffect(() => {
        if (architectures.length === 0) queryArchitectures()
    }, [architectures.length]);

    function queryArchitectures() {
        if (loading) return;

        setLoading(true);

        sendRequest(Endpoint.queryArchitectures(auth.user.id_token))
            .then(handleResponse)
            .then(response => architectureDataStore.setMany(response.Items))
            .catch((error) => {console.error(error.message)})
            .finally(() => setLoading(false));
    }

    return (
        <ContentLayout
            headerVariant={'high-contrast'}
            maxContentWidth={1200}
            disableOverlap
            header={
                <CustomHeader />
            }
        >
            <Box margin={{top: 'xl'}}>
                <ArchitectureCards
                    items={architectures}
                    loading={loading}
                    onRefreshClick={queryArchitectures}
                />
            </Box>
        </ContentLayout>
    );
}
